import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import IosShareIcon from '@mui/icons-material/IosShare';
import { isCustomer } from '../../../api';
import { trackEvent } from '../../../analytics';
import { publishHangoutInvite, toApiError } from '../api';
import { useHangoutInvite } from '../hooks';
import { errorTitle, inviteUrl, loginUrlFor } from '../utils';
import { ErrorState, InviteStatusBadge, PageTop, Skeleton } from '../components/common';
import { InvitePreviewCard } from '../components/InviteParts';
import { ShareInviteSheet } from '../components/ShareInviteSheet';
import { forgetDraft } from './CreateInvitePage';
import '../hangouts.css';

// Screen 3 "Invite Preview" (/invites/:inviteId/preview): what friends will see. Sharing a draft
// publishes it first (Draft → Active), which is what makes the public link work.
const InvitePreviewPage: React.FC = () => {
  const { inviteId } = useParams<{ inviteId: string }>();
  const id = Number(inviteId) || null;
  const location = useLocation();
  const navigate = useNavigate();
  const { data: invite, error, loading, setData } = useHangoutInvite(isCustomer() ? id : null);
  const [sharing, setSharing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (invite) trackEvent('hangout_invite_previewed', { status: invite.status });
  }, [invite]);

  if (!isCustomer()) return <Navigate to={loginUrlFor(location.pathname)} replace />;

  const handleShare = async () => {
    if (!invite) return;
    setNotice(null);
    if (invite.status === 'Draft') {
      setPublishing(true);
      try {
        const published = await publishHangoutInvite(invite.id);
        setData(published);
        forgetDraft(published.venue.id);
        trackEvent('hangout_invite_published', { invite_type: published.inviteType });
        setNotice('Your invite is live. Share it with your friends!');
      } catch (err) {
        setNotice(toApiError(err).message);
        return;
      } finally {
        setPublishing(false);
      }
    }
    setSharing(true);
  };

  if (error) {
    const e = errorTitle(error);
    return <main className="hg"><div className="hg-wrap"><PageTop title="Invite Preview" /><ErrorState title={e.title} body={e.body} /></div></main>;
  }

  return (
    <main className="hg">
      <div className="hg-wrap">
        <PageTop title="Invite Preview" right={invite && <InviteStatusBadge status={invite.status} />} />
        {loading || !invite ? <Skeleton height={560} /> : (
          <>
            {notice && <div className={`hg-notice ${notice.startsWith('Your invite is live') ? 'success' : 'error'}`} role="status">{notice}</div>}
            <InvitePreviewCard invite={invite}>
              {invite.status === 'Active'
                ? <a className="hg-btn hg-gold block" style={{ marginTop: 16 }} href={inviteUrl(invite.publicToken)} target="_blank" rel="noreferrer">Join on Cheers2Night</a>
                : <button type="button" className="hg-btn hg-gold block" style={{ marginTop: 16 }} disabled>Join on Cheers2Night</button>}
            </InvitePreviewCard>
            {invite.status === 'Draft' && <p className="hg-muted" style={{ textAlign: 'center', fontSize: 13 }}>This is a draft. Friends can open it once you share it.</p>}
          </>
        )}
      </div>

      {invite && invite.isHost && (
        <div className="hg-sticky">
          <div className="hg-wrap" style={{ flexDirection: 'row' }}>
            {(invite.status === 'Draft' || invite.status === 'Active') && (
              <button type="button" className="hg-btn hg-outline" style={{ flex: 1 }} onClick={() => navigate(`/invites/${invite.id}/edit`)}><EditOutlinedIcon />Edit Invite</button>
            )}
            {(invite.status === 'Draft' || invite.status === 'Active') ? (
              <button type="button" className="hg-btn hg-gold" style={{ flex: 1 }} disabled={publishing} onClick={handleShare}>
                <IosShareIcon />{publishing ? 'Publishing…' : invite.status === 'Draft' ? 'Publish & Share' : 'Share Invite'}
              </button>
            ) : (
              <Link className="hg-btn hg-outline block" to="/profile/invites">Back to My Invites</Link>
            )}
          </div>
        </div>
      )}

      {sharing && invite && (
        <ShareInviteSheet invite={invite} publicToken={invite.publicToken} refCode={invite.isHost ? null : invite.myRefCode} onClose={() => setSharing(false)} />
      )}
    </main>
  );
};

export default InvitePreviewPage;
